import { AggregatedReferenceType } from '@ardoq/data-model';
import {
  ArdoqId,
  GroupType,
  GroupingRule,
  LinkedComponent,
} from '@ardoq/api-types';
import {
  ComponentOrGroupId,
  GroupMap,
  ParentGroupMap,
  PathObject,
} from '../../types';
import { last } from 'lodash';
import { logWarn } from '@ardoq/logging';
import type { GraphInterface } from '../../graphInterface/graphInterface';
import { type I18nLocale, t } from '@ardoq/i18n';

type DepthFirstSearchResult = {
  visiting: Set<ArdoqId>;
  visited: Set<ArdoqId>;
  parents: Map<ArdoqId, Set<ArdoqId>>;
  error: string | null;
};

export const getAllPaths = (
  componentIds: ArdoqId[],
  linkedComponentsMap: Map<string, LinkedComponent[]>,
  referencesToProcess: Set<ArdoqId>,
  i18nLocale: I18nLocale
): { paths: string[][]; error: string | null } => {
  const visiting = new Set<ArdoqId>();
  const visited = new Set<ArdoqId>();
  const parents = new Map<ArdoqId, Set<ArdoqId>>();
  const error: string | null = null;
  const depthFirstSearchResult: DepthFirstSearchResult = {
    visiting,
    visited,
    parents,
    error,
  };
  componentIds.forEach(componentId =>
    depthFirstSearch(
      componentId,
      linkedComponentsMap,
      referencesToProcess,
      depthFirstSearchResult,
      i18nLocale
    )
  );

  if (depthFirstSearchResult.error) {
    return { paths: [], error: depthFirstSearchResult.error };
  }

  const allParents = new Set(
    Array.from(parents.values()).flatMap(allParents => Array.from(allParents))
  );

  const leafNodes = componentIds.filter(
    componentId => !allParents.has(componentId)
  );

  const paths = leafNodes.flatMap(node => buildPath(node, parents));

  return { paths, error: null };
};

const depthFirstSearch = (
  componentId: ArdoqId,
  linkedComponentsMap: Map<string, LinkedComponent[]>,
  referencesToProcess: Set<ArdoqId>,
  depthFirstSearchResult: DepthFirstSearchResult,
  i18nLocale: I18nLocale
) => {
  if (depthFirstSearchResult.error) {
    return;
  }
  if (depthFirstSearchResult.visiting.has(componentId)) {
    depthFirstSearchResult.error = t(
      i18nLocale,
      'graph:groupingErrors.cyclicDependency',
      'The references to group by cannot have a cyclic dependency'
    );
    return;
  }
  if (depthFirstSearchResult.visited.has(componentId)) {
    return;
  }
  depthFirstSearchResult.visiting.add(componentId);
  const connectedComponents = (
    linkedComponentsMap.get(componentId) || []
  ).filter(({ referenceId }) => referencesToProcess.has(referenceId));

  if (depthFirstSearchResult.parents.has(componentId)) {
    logWarn(Error('Visiting a node twice in depth first search'));
  }
  depthFirstSearchResult.parents.set(componentId, new Set());

  connectedComponents.forEach(({ componentId: connectedComponentId }) => {
    // Disregard self references.
    if (connectedComponentId === componentId) {
      return;
    }
    depthFirstSearchResult.parents.get(componentId)!.add(connectedComponentId);
    depthFirstSearch(
      connectedComponentId,
      linkedComponentsMap,
      referencesToProcess,
      depthFirstSearchResult,
      i18nLocale
    );
  });
  depthFirstSearchResult.visiting.delete(componentId);
  depthFirstSearchResult.visited.add(componentId);
};

const buildPath = (
  componentId: ArdoqId,
  parentsMap: Map<ArdoqId, Set<ArdoqId>>,
  path: ArdoqId[] = []
): ArdoqId[][] => {
  const currentPath = [componentId, ...path];
  const parents = Array.from(parentsMap.get(componentId) || []);
  if (parents.length > 0) {
    return parents.flatMap(parent =>
      buildPath(parent, parentsMap, currentPath)
    );
  }
  return currentPath.length > 1 ? [currentPath] : [];
};

const getReferenceTypeNameForGroupByTargetId = (
  referenceTypes: AggregatedReferenceType[],
  targetId?: string
) => {
  if (!targetId) {
    return '';
  }
  return (
    referenceTypes.find(({ typeIds }) => typeIds.includes(targetId))?.name || ''
  );
};

export const getGroupRuleLabel = (
  groupBy: GroupingRule,
  referenceTypes: AggregatedReferenceType[]
) => {
  const { type, sublabel, targetId } = groupBy;
  switch (type) {
    case GroupType.REFERENCE:
      return (
        sublabel ||
        getReferenceTypeNameForGroupByTargetId(referenceTypes, targetId)
      );
    case GroupType.REFERENCE_TYPE_NAME:
      return sublabel || targetId;
    case GroupType.PARENT:
    case GroupType.PARENT_ALL:
      return sublabel || '';
  }

  return '';
};

export const setParentsAndAddGroupIfNeeded = (
  graphInterface: GraphInterface,
  path: string[],
  currentParentGroupId: string | null,
  type: GroupType,
  subLabel: string,
  groupMapRaw: GroupMap,
  parentGroupMap: ParentGroupMap
) =>
  path.reduce((parentId, componentId) => {
    const label = graphInterface.getComponentRawLabel(componentId) ?? '';
    const groupIdPrefix = componentId;
    if (componentId !== last(path) && !groupMapRaw.has(groupIdPrefix)) {
      groupMapRaw.set(groupIdPrefix, {
        id: '',
        label,
        modelId: componentId,
        subLabels: [subLabel],
        type,
      });
    }
    if (parentId) {
      updateParent(componentId, parentId, parentGroupMap, currentParentGroupId);
    }
    return componentId;
  }, currentParentGroupId);

export const updateParent = (
  id: string,
  parentId: string,
  parentGroupMap: ParentGroupMap,
  currentParent?: string | null
) => {
  const parents = getParents(id, parentGroupMap);
  if (currentParent) {
    parents.delete(currentParent);
  }
  parents.add(parentId);
};

export const removeParent = (
  id: string,
  parentId: string,
  parentGroupMap: ParentGroupMap
) => getParents(id, parentGroupMap)?.delete(parentId);

export const clearBaseGroup = (
  baseGroupId: ComponentOrGroupId,
  path: ArdoqId[],
  parentGroupMap: ParentGroupMap
) => {
  path.forEach(id => removeParent(id, baseGroupId, parentGroupMap));
};

const getParents = (id: string, parentGroupMap: ParentGroupMap) => {
  let parents = parentGroupMap.get(id);
  if (!parents) {
    parents = new Set();
    parentGroupMap.set(id, parents);
  }
  return parents;
};

export const toPathObject = (
  graphInterface: GraphInterface,
  path: string[],
  sublabels: string[][]
): PathObject => {
  const headsPath = path.slice(0, -1);
  return {
    path,
    ids: new Set(path),
    tail: path[path.length - 1],
    refTypeHead: headsPath.find(graphInterface.isComponent) ?? '',
    heads: new Set(headsPath),
    sublabels,
  };
};

export const toNamedPair = (
  graphInterface: GraphInterface,
  parentGroupMap: ParentGroupMap,
  groupMapRaw: GroupMap,
  componentId: ArdoqId,
  i18nLocale: I18nLocale,
  toNonComponentAncestors: (
    ancestors?: Set<string>
  ) => Set<string> = ancestors => ancestors || new Set()
) => {
  const parentGroupIds = toNonComponentAncestors(
    parentGroupMap.get(componentId)
  );
  const parentNames =
    parentGroupIds.size > 0
      ? parentSetToNames(parentGroupIds, groupMapRaw, i18nLocale)
      : t(i18nLocale, 'graph:groupingErrors.notGrouped', 'Not grouped');
  return `${toName(graphInterface, componentId)} - ${parentNames}`;
};

const parentSetToNames = (
  parentSet: Set<string>,
  groupMapRaw: GroupMap,
  i18nLocale: I18nLocale
) =>
  `(${[...parentSet]
    .map(
      groupId =>
        groupMapRaw.get(groupId)?.label ??
        t(i18nLocale, 'graph:groupingErrors.missingLabel', 'Missing label')
    )
    .join(', ')})`;

export const toName = (graphInterface: GraphInterface, componentId: ArdoqId) =>
  graphInterface.getComponentAttribute(componentId, 'name') ?? '';

export const repeat = <T>(item: T, count: number): T[] =>
  Array(count).fill(item);

export const areSamePaths = (pathA: string[], pathB: string[]) =>
  pathA.length === pathB.length && pathStartsWithPath(pathA, pathB);

export const pathStartsWithPath = (pathA: string[], pathB: string[]) =>
  pathB.length <= pathA.length &&
  pathB.every((id, index) => id === pathA[index]);

export const isParentTypeGrouping = (type?: GroupType) =>
  type === GroupType.PARENT_ALL || type === GroupType.PARENT;
